import moment from 'moment';
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const zStr = (fallback = '') => z.string().optional().default(fallback);

const zBool = (fallback: boolean) =>
    z
        .string()
        .optional()
        .default(String(fallback))
        .transform((v: string) => v === 'true');

const zNum = (fallback: number) =>
    z
        .string()
        .optional()
        // A declared-but-empty var ("FOO=") reaches us as "", which Number() would
        // quietly coerce to 0. Blank is treated as unset so the default survives.
        .transform((v?: string) => (v === undefined || v.trim() === '' ? fallback : Number(v)))
        .pipe(
            z
                .number({ invalid_type_error: 'must be a number' })
                .int('must be a whole number')
                .nonnegative('must be zero or greater')
        );

/** Spells a duration out in units so the seconds figure is not a magic product. */
const durationSeconds = (input: moment.DurationInputObject) =>
    moment.duration(input).asSeconds();

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

const envSchema = z.object({
    // App / Environment
    REACT_APP_ENV: zStr("development"),
    REACT_APP_URL: zStr("http://localhost:4200"),

    // Backend API
    REACT_APP_BACKEND_URL: zStr("http://localhost:7000"),
    REACT_APP_GQL_PATH: zStr("/api/gql"),
    REACT_APP_REST_PATH: zStr("/api/rest"),

    // Stripe
    REACT_APP_STRIPE_PUBLIC_KEY: zStr(""),

    // Sentry
    REACT_APP_SENTRY_DSN: zStr(""),

    // Vercel (auto-exposed by Vercel for CRA builds, e.g. "production" | "preview" | "development")
    REACT_APP_VERCEL_ENV: zStr(""),

    // Intercom
    REACT_APP_INTERCOM_ENABLED: zBool(false),
    REACT_APP_INTERCOM_APP_ID: zStr(""),

    // MUI License
    REACT_APP_MUI_X_LICENSE_KEY: zStr(""),

    // Links
    REACT_APP_IOS_LISTING: zStr(""),
    REACT_APP_ANDROID_LISTING: zStr(""),
    REACT_APP_MOBILE_ASSESSMENT_DEEPLINK: zStr(""),
    REACT_APP_PROVIDER_NOTION_PAGE: zStr(""),
    REACT_APP_BETTERMODE_URL: zStr(""),

    // Assets
    REACT_APP_DEFAULT_CAREBOT_PHOTO_URL: zStr(""),

    // Health Status
    REACT_APP_HEALTH_STATUS_API: zStr(""),

    // IP Info
    REACT_APP_IP_INFO_LITE_URL: zStr(""),
    REACT_APP_IP_INFO_TOKEN: zStr(""),

    // Auth session expiry. Values are seconds; the two thresholds are measured as
    // "time still left on the token", so a larger number fires earlier.
    REACT_APP_AUTH_WARN_THRESHOLD_SECONDS: zNum(durationSeconds({ hours: 2, minutes: 15 })),
    REACT_APP_AUTH_BLOCK_THRESHOLD_SECONDS: zNum(durationSeconds({ hours: 2 })),
    // Testing only: pretend the session ends this many seconds from page load so
    // both phases are reachable without waiting out a real multi-day token.
    // 0 disables the override, and it is ignored outright in production.
    REACT_APP_AUTH_DEBUG_SESSION_SECONDS: zNum(0),

    // Feature Flags
    REACT_APP_COMMUNITY_ENABLED: zBool(false),
    REACT_APP_ENABLE_MULTIAPPOINTMENT: zBool(true),
    REACT_APP_DISABLE_HEALTH_POLLER: zBool(false),
}).refine(
    (v) =>
        v.REACT_APP_AUTH_WARN_THRESHOLD_SECONDS >= v.REACT_APP_AUTH_BLOCK_THRESHOLD_SECONDS,
    {
        // Blocking before the warning ever shows would drop the user straight into
        // the locked state with no heads-up, so the ordering is enforced here.
        message:
            'REACT_APP_AUTH_WARN_THRESHOLD_SECONDS must be >= REACT_APP_AUTH_BLOCK_THRESHOLD_SECONDS',
        path: ['REACT_APP_AUTH_WARN_THRESHOLD_SECONDS'],
    }
);

// ---------------------------------------------------------------------------
// Parse & export
// ---------------------------------------------------------------------------

const parsed = envSchema.safeParse(process.env);

if (!parsed.success) {
    console.error(
        "❌ Invalid environment variables:",
        parsed.error.flatten().fieldErrors
    );
    throw new Error("Invalid environment variables");
}

const raw = parsed.data;

// ---------------------------------------------------------------------------
// Structured env object
// ---------------------------------------------------------------------------

export const env = {
    /** REACT_APP_ENV value */
    appEnv: raw.REACT_APP_ENV,
    /** Frontend URL */
    appUrl: raw.REACT_APP_URL,
    /** Convenience booleans */
    isDev: raw.REACT_APP_ENV === "development",
    isProd: raw.REACT_APP_ENV === "production",
    isStaging: raw.REACT_APP_ENV === "staging",

    backend: {
        url: raw.REACT_APP_BACKEND_URL,
        gqlPath: raw.REACT_APP_GQL_PATH,
        restPath: raw.REACT_APP_REST_PATH,
        /** Full GraphQL API URL */
        gqlApi: `${raw.REACT_APP_BACKEND_URL}${raw.REACT_APP_GQL_PATH}`,
        /** Full REST API URL */
        restApi: `${raw.REACT_APP_BACKEND_URL}${raw.REACT_APP_REST_PATH}`,
    },

    stripe: {
        publicKey: raw.REACT_APP_STRIPE_PUBLIC_KEY,
    },

    /** True when running on a Vercel deployment */
    isVercel: raw.REACT_APP_VERCEL_ENV !== "",

    sentry: {
        dsn: raw.REACT_APP_SENTRY_DSN,
        /** Sentry environment: force "staging" on Vercel, otherwise mirror REACT_APP_ENV */
        environment: raw.REACT_APP_VERCEL_ENV !== "" ? "staging" : raw.REACT_APP_ENV,
    },

    intercom: {
        enabled: raw.REACT_APP_INTERCOM_ENABLED,
        appId: raw.REACT_APP_INTERCOM_APP_ID,
    },

    muiLicenseKey: raw.REACT_APP_MUI_X_LICENSE_KEY,

    links: {
        iosListing: raw.REACT_APP_IOS_LISTING,
        androidListing: raw.REACT_APP_ANDROID_LISTING,
        mobileAssessmentDeeplink: raw.REACT_APP_MOBILE_ASSESSMENT_DEEPLINK,
        providerNotionPage: raw.REACT_APP_PROVIDER_NOTION_PAGE,
        bettermodeUrl: raw.REACT_APP_BETTERMODE_URL,
    },

    assets: {
        defaultCarebotPhoto: raw.REACT_APP_DEFAULT_CAREBOT_PHOTO_URL,
    },

    healthStatusApi: raw.REACT_APP_HEALTH_STATUS_API,

    ipInfo: {
        liteUrl: raw.REACT_APP_IP_INFO_LITE_URL,
        token: raw.REACT_APP_IP_INFO_TOKEN,
    },

    auth: {
        /** Seconds of remaining token life at which the expiry warning appears */
        warnThresholdSeconds: raw.REACT_APP_AUTH_WARN_THRESHOLD_SECONDS,
        /** Seconds of remaining token life at which the app blocks until re-auth */
        blockThresholdSeconds: raw.REACT_APP_AUTH_BLOCK_THRESHOLD_SECONDS,
        /**
         * Testing override, in seconds: treat the session as ending this long after
         * page load instead of reading the token's `exp`. 0 disables it, and it is
         * forced off in production so a stray var cannot lock real users out.
         */
        debugSessionSeconds:
            raw.REACT_APP_ENV === 'production' ? 0 : raw.REACT_APP_AUTH_DEBUG_SESSION_SECONDS,
    },

    features: {
        communityEnabled: raw.REACT_APP_COMMUNITY_ENABLED,
        multiAppointment: raw.REACT_APP_ENABLE_MULTIAPPOINTMENT,
        disableHealthPoller: raw.REACT_APP_DISABLE_HEALTH_POLLER,
    },
} as const;

export type Env = typeof env;
